if (!customElements.get('theme-newsletter-form')) {
  class ThemeNewsletterForm extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this._controller = new AbortController();
      const { signal } = this._controller;

      this.form = this.querySelector('form');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitButton = this.querySelector('[data-newsletter-submit]');
      this.spinner = this.querySelector('.loading__spinner');
      this.statusEl = this.querySelector('[data-newsletter-status]');
      this.thanksEl = this.querySelector('[data-newsletter-thanks]');

      this.integration = this.dataset.integration || 'shopify';
      this.klaviyoList = this.dataset.klaviyoList || '';
      this.mailchimpUrl = this.dataset.mailchimpUrl || '';
      this.doubleOptIn = this.dataset.doubleOptIn === 'true';
      this.postSignupAction = this.dataset.postSignup || 'thanks_message';
      this.redirectUrl = this.dataset.redirectUrl || '';

      this.ensureFieldError();
      this.form?.addEventListener('submit', this.handleSubmit, { signal });
    }

    disconnectedCallback() {
      this._controller?.abort();
    }

    ensureFieldError() {
      const wrapper = this.emailInput?.closest('.form-field');
      if (!wrapper || !this.emailInput?.id) return;

      const errorId = `${this.emailInput.id}-error`;
      let errorEl = wrapper.querySelector(`#${errorId}`);

      if (!errorEl) {
        errorEl = document.createElement('p');
        errorEl.id = errorId;
        errorEl.className = 'form-field__error';
        errorEl.setAttribute('role', 'alert');
        errorEl.hidden = true;
        wrapper.appendChild(errorEl);
      }

      const describedby = this.emailInput.getAttribute('aria-describedby') || '';
      if (!describedby.split(' ').includes(errorId)) {
        this.emailInput.setAttribute('aria-describedby', describedby ? `${describedby} ${errorId}` : errorId);
      }

      this.fieldErrorEl = errorEl;
      this.fieldWrapper = wrapper;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.isLoading) return;

      const email = this.emailInput?.value.trim();
      if (!this.isValidEmail(email)) {
        this.handleError(theme.locales.newsletter.error, { silent: true });
        return;
      }

      this.clearFieldError();
      this.setLoading(true);
      theme.pubsub?.publish(theme.pubsub.events.FORM_SUBMIT, { integration: this.integration });

      try {
        if (this.integration === 'klaviyo') {
          await this.submitKlaviyo(email);
        } else if (this.integration === 'mailchimp') {
          await this.submitMailchimp(email);
        } else {
          await this.submitShopify();
        }
      } finally {
        this.setLoading(false);
      }
    }

    isValidEmail(email) {
      return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    async submitShopify() {
      const formData = new FormData(this.form);

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData,
          redirect: 'follow',
        });
        const html = await response.text();
        const parsedRoot = theme.utils.domParser.parseFromString(html, 'text/html').getElementById(this.id);
        const succeeded = !!parsedRoot?.querySelector('[data-newsletter-success]');

        if (succeeded) {
          this.handleSuccess();
          return;
        }

        const parsedError = parsedRoot?.querySelector('.form-field__error');
        this.handleError(parsedError?.textContent?.trim() || theme.locales.newsletter.error);
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    async submitKlaviyo(email) {
      if (!this.klaviyoList) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      try {
        const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe_external', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ g: this.klaviyoList, email }).toString(),
        });
        const data = await response.json().catch(() => null);

        if (response.ok && data?.success !== false) {
          this.handleSuccess();
          return;
        }

        const message = this.isAlreadySubscribed(data) ? theme.locales.newsletter.alreadySubscribed : theme.locales.newsletter.error;
        this.handleError(message);
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    isAlreadySubscribed(data) {
      const message = JSON.stringify(data?.errors || data?.message || '').toLowerCase();
      return message.includes('already') || message.includes('suscri') || message.includes('assinad');
    }

    async submitMailchimp(email) {
      if (!this.mailchimpUrl) {
        this.handleError(theme.locales.newsletter.error);
        return;
      }

      const formData = new FormData();
      formData.append('EMAIL', email);

      try {
        // Classic Mailchimp embedded forms don't support CORS: the response is opaque
        // under mode "no-cors" and cannot be inspected. A completed request is the
        // only signal available, so it's treated as an optimistic success.
        await fetch(this.mailchimpUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: formData,
        });
        this.handleSuccess();
      } catch (error) {
        this.handleError(theme.locales.form.error.network);
      }
    }

    handleSuccess() {
      const message = this.decodeEntities(
        this.doubleOptIn ? theme.locales.newsletter.doubleOptInMessage : theme.locales.newsletter.success
      );

      theme.toast?.show({ type: 'success', message });
      this.announce(message);
      theme.pubsub?.publish(theme.pubsub.events.FORM_SUCCESS, { integration: this.integration });

      if (this.postSignupAction === 'redirect' && this.redirectUrl) {
        window.location.href = this.redirectUrl;
        return;
      }

      this.showThanks(message);
    }

    handleError(message, { silent = false } = {}) {
      message = this.decodeEntities(message);
      if (!silent) {
        theme.toast?.show({ type: 'error', message });
        theme.pubsub?.publish(theme.pubsub.events.FORM_ERROR, { integration: this.integration, error: message });
      }

      this.announce(message);
      this.showFieldError(message);
    }

    showThanks(message) {
      if (!this.form || !this.thanksEl) return;
      this.form.hidden = true;
      this.thanksEl.textContent = message;
      this.thanksEl.hidden = false;
    }

    showFieldError(message) {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.textContent = message;
        this.fieldErrorEl.hidden = false;
      }
      this.fieldWrapper?.classList.add('form-field--error');
      this.emailInput?.setAttribute('aria-invalid', 'true');
    }

    clearFieldError() {
      if (this.fieldErrorEl) {
        this.fieldErrorEl.hidden = true;
        this.fieldErrorEl.textContent = '';
      }
      this.fieldWrapper?.classList.remove('form-field--error');
      this.emailInput?.removeAttribute('aria-invalid');
    }

    announce(message) {
      if (this.statusEl) this.statusEl.textContent = message;
    }

    decodeEntities(str) {
      if (!str || str.indexOf('&') === -1) return str;
      const el = document.createElement('textarea');
      el.innerHTML = str;
      return el.value;
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.submitButton?.toggleAttribute('disabled', isLoading);
      this.submitButton?.setAttribute('aria-busy', String(isLoading));
      this.spinner?.classList.toggle('hidden', !isLoading);
      if (this.emailInput) this.emailInput.disabled = isLoading;
    }
  }

  customElements.define('theme-newsletter-form', ThemeNewsletterForm);
}
